import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, useTheme, CircularProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { addNote, setNotes } from '../../../../../features/notes/notesSlice';
import noteService from '../../../../../utils/apiRequests/noteService';
import NotesDashboard from './NotesDashboard';
import TimelineView from './TimelineView';
import NotesListView from './NotesListView';
import NotesCorkboardView from './NotesCorkboardView';

/**
 * Normalize backend note data to frontend format
 * Backend uses _id, frontend uses id
 * Backend sends Date objects as ISO strings in JSON, convert to epoch timestamps
 */
const normalizeNote = (backendNote) => {
  if (!backendNote) return null;

  // Helper function to convert date to epoch timestamp
  const toEpoch = (dateValue) => {
    if (!dateValue) return Date.now();
    if (typeof dateValue === 'string') {
      return new Date(dateValue).getTime();
    }
    return dateValue.getTime?.() || dateValue;
  };

  const normalized = {
    ...backendNote,
    id: backendNote._id || backendNote.id,
    _id: backendNote._id || backendNote.id,
    // Convert ISO date strings to epoch timestamps
    createdAt: toEpoch(backendNote.createdAt),
    updatedEpoch: toEpoch(backendNote.updatedAt),
    updatedAt: toEpoch(backendNote.updatedAt),
  };

  return normalized;
};

const NotesCenter = ({ notesView = 'dashboard', onNoteSelect }) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const notes = useSelector((state) => state.notes.notes);
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);

  // Load notes from backend on mount
  useEffect(() => {
    const loadNotes = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch all notes for the project (standalone notes without a parent)
        // Notes with parents (like corkboard notes) are loaded separately when their parent is loaded
        const response = await noteService.getNotes(projectId);

        if (response.error) {
          console.error('Error loading notes:', response.error);
          // If error, keep existing state (might be from persistence)
          setLoading(false);
          return;
        }

        if (response.data?.notes) {
          const normalizedNotes = response.data.notes.map(normalizeNote).filter(Boolean);
          dispatch(setNotes(normalizedNotes));
        }
      } catch (err) {
        console.error('Error loading notes:', err);
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [projectId, dispatch]);

  const handleAddNote = async () => {
    if (!projectId) {
      console.error('Cannot add note: projectId is missing');
      return;
    }

    const newNoteData = {
      title: 'New Note',
      content: '',
      category: 'General',
      color: isDark ? '#4a5568' : '#fef3c7',
      textColor: isDark ? '#ffffff' : '#000000',
      featureContext: 'general',
    };

    try {
      const response = await noteService.createNote(projectId, newNoteData);

      if (response.error) {
        console.error('Error creating note:', response.error);
        // Still add to Redux for optimistic update (if offline)
        const tempNote = {
          ...newNoteData,
          _id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: Date.now(),
          updatedEpoch: Date.now(),
        };
        dispatch(addNote(tempNote));
        return;
      }

      if (response.data?.data) {
        const normalizedNote = normalizeNote(response.data.data);
        if (normalizedNote) {
          dispatch(addNote(normalizedNote));
        }
      }
    } catch (err) {
      console.error('Error creating note:', err);
      // Still add to Redux for optimistic update
      const tempNote = {
        ...newNoteData,
        _id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: Date.now(),
        updatedEpoch: Date.now(),
      };
      dispatch(addNote(tempNote));
    }
  };

  const renderView = () => {
    switch (notesView) {
      case 'dashboard':
        return <NotesDashboard notes={notes} />;
      case 'timeline':
        return <TimelineView notes={notes} onNoteSelect={onNoteSelect} />;
      case 'list':
        return <NotesListView notes={notes} onNoteSelect={onNoteSelect} />;
      case 'corkboard':
        return <NotesCorkboardView notes={notes} onNoteSelect={onNoteSelect} />;
      default:
        return <NotesDashboard notes={notes} onNoteSelect={onNoteSelect} />;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 3, pb: 0 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNote} sx={{ mb: 2 }}>
          Add Note
        </Button>
      </Box>
      {renderView()}
    </Box>
  );
};

export default NotesCenter;

NotesCenter.propTypes = {
  notesView: PropTypes.string,
  onNoteSelect: PropTypes.func,
};

NotesCenter.defaultProps = {
  notesView: 'dashboard',
  onNoteSelect: null,
};
